import { BaseResponseSchema } from '@monetize/types/common';
import { boolean, string, z } from 'zod';
import { BucketResponseSchema } from './bucket.types';
import { CustomFieldRecordSchema } from './customFields.types';
import { ProductTypeEnumZ } from './enums.types';
import { UsageTypeResSchema } from './usage.types';

export enum PrePaidCreditConfigTypeEnum {
  ALL_PRODUCTS = 'ALL_PRODUCTS',
  USAGE_PRODUCTS = 'USAGE_PRODUCTS',
  SUBSCRIPTION_PRODUCTS_ONLY = 'SUBSCRIPTION_PRODUCTS_ONLY',
  SPECIFIC_PRODUCTS = 'SPECIFIC_PRODUCTS',
}
export enum ProductStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export enum RecognitionMethodEnum {
  IMMEDIATE = 'IMMEDIATE',
  STRAIGHT_LINE_EXACT_DAYS = 'STRAIGHT_LINE_EXACT_DAYS',
  STRAIGHT_LINE_EVEN_PERIODS = 'STRAIGHT_LINE_EVEN_PERIODS',
  STRAIGHT_LINE_PRORATED_FIRST_LAST = 'STRAIGHT_LINE_PRORATED_FIRST_LAST',
  STRAIGHT_LINE_PRORATED_PERIOD_RATE = 'STRAIGHT_LINE_PRORATED_PERIOD_RATE',
}
export const ProductStatusEnumZ = z.enum(ProductStatusEnum);

export const PrePaidCreditConfigTypeEnumZ = z.enum(PrePaidCreditConfigTypeEnum);
export const RecognitionMethodEnumZ = z.enum(RecognitionMethodEnum);

export enum ProductQuantityDisplayTypeEnum {
  INPUT = 'INPUT',
  DROPDOWN = 'DROPDOWN',
}
export const ProductQuantityDisplayTypeEnumZ = z.enum(
  ProductQuantityDisplayTypeEnum,
);

export enum ProrationPolicyEnum {
  NON_PRORATED = 'NON_PRORATED',
  PRORATED = 'PRORATED',
}

// from POST /api/products
export const ProductSchema = z.object({
  id: z.string().optional(),
  prorationPolicy: z.enum(ProrationPolicyEnum).nullable(),
  customId: z.string().max(255).nullish(),
  financeId: z.string().max(100).nullish(),
  name: z
    .string({
      error: 'Product name is required',
    })
    .min(1, { message: 'Product name cannot be empty' })
    .max(255, {
      message: 'Product name cannot be longer than 255 characters',
    }),
  description: z.string().max(255).nullish(),
  status: ProductStatusEnumZ,
  productType: ProductTypeEnumZ,
  usageTypeIds: z.array(z.string()).nullish(),
  sku: z.string().max(255).nullish(),
  // TODO: we should make this required, but requires all DB entries to be updated
  recognitionMethod: RecognitionMethodEnumZ.nullish(),
  recognitionDuration: z.number().nullish(),
  oneTimePrepaidCreditConfig: z
    .object({
      type: PrePaidCreditConfigTypeEnumZ,
    })
    .nullish(),
  ppuDisplayPrecision: z.number().min(0).max(10).nullish(),
  customFields: CustomFieldRecordSchema.optional(),
  taxItemCode: z.string().max(100).nullish(),
  taxExempt: z.boolean().catch(false),
  bucketId: z.string().nullish(),
  displayUnitPriceFrequency: z.number().min(1).max(60).nullish(),
});

export type IProductSchema = z.infer<typeof ProductSchema>;
export type IProduct = IProductSchema;
export type IProductWithQuantityDisplayType = IProduct & {
  quantityDisplayType?: ProductQuantityDisplayTypeEnum;
};

export const ProductResSchema = BaseResponseSchema.extend({
  customId: string().nullable(),
  financeId: string().nullable(),
  name: string(),
  description: string().nullable(),
  status: ProductStatusEnumZ,
  productType: ProductTypeEnumZ,
  ppuDisplayPrecision: z.number().min(0).max(10).nullish(),
  usageTypes: z.array(UsageTypeResSchema).nullish(),
  // TODO: we should make this required, but requires all DB entries to be updated
  recognitionMethod: RecognitionMethodEnumZ.nullish(),
  sku: string().nullable(),
  locked: boolean(),
  customFields: CustomFieldRecordSchema.optional(),
  taxItemCode: z.string().nullish(),
  isMandatory: z.boolean().default(false),
  isSelected: z.boolean().default(false),
  taxExempt: z.boolean().catch(false),
  quantityDisplayType: ProductQuantityDisplayTypeEnumZ.optional().default(
    ProductQuantityDisplayTypeEnum.INPUT,
  ),
  bucket: BucketResponseSchema.nullish(),
  prorationPolicy: z.enum(ProrationPolicyEnum).nullable(),
  displayUnitPriceFrequency: z.number().min(1).max(60).nullish(),
  oneTimePrepaidCreditConfig: z
    .object({
      type: PrePaidCreditConfigTypeEnumZ,
    })
    .nullable(),
});
export type IProductResSchema = z.infer<typeof ProductResSchema>;

export const ProductResWithMandatorySchema = ProductResSchema.extend({
  isMandatory: z.boolean().default(false),
  isSelected: z.boolean().default(false),
  position: z.number(),
  bucketId: z.string().nullish(),
  isPriceTierLockEligible: z.boolean().optional().default(false),
  quantityDisplayType: ProductQuantityDisplayTypeEnumZ.optional().default(
    ProductQuantityDisplayTypeEnum.INPUT,
  ),
});
export type ProductResWithMandatory = z.infer<
  typeof ProductResWithMandatorySchema
>;

export const ProductSchemaUI = ProductSchema.extend({
  prices: z.array(z.any()).nullish(),
  createDate: z.union([z.string(), z.date()]).nullish(),
  createdBy: string().nullish(),
  modifyDate: string().nullish(),
  lastModifiedBy: string().nullish(),
});
export type IProductSchemaUI = z.infer<typeof ProductSchemaUI>;

export const SelectedProductsWithinOfferingSchema = ProductResSchema.pick({
  id: true,
  name: true,
  description: true,
  productType: true,
}).extend({
  quoteItemId: z.string().nullish(),
  isMandatory: z.boolean(),
  isSelected: z.boolean(),
  position: z.number(),
});
export type SelectedProductsWithinOffering = z.infer<
  typeof SelectedProductsWithinOfferingSchema
>;

// wrapped in an object to make it easier to use with react-hook-form
export const SelectedProductsWithinOfferingFormSchema = z.object({
  products: SelectedProductsWithinOfferingSchema.array().refine(
    (values) => values.some(({ isSelected }) => isSelected),
    { message: `At least one product must be selected` },
  ),
});
export type SelectedProductsWithinOfferingForm = z.infer<
  typeof SelectedProductsWithinOfferingFormSchema
>;

const ProductAdvancedSettingsItemSchema = z.object({
  quantityDisplayType: ProductQuantityDisplayTypeEnumZ.default(
    ProductQuantityDisplayTypeEnum.INPUT,
  ),
  isPriceTierLockEligible: z.boolean().default(false),
});

export const ProductAdvancedSettingsSchema = z.object({
  products: z.array(ProductAdvancedSettingsItemSchema),
});

export type IProductAdvancedSettingsInput = z.input<
  typeof ProductAdvancedSettingsSchema
>;
export type IProductAdvancedSettings = z.infer<
  typeof ProductAdvancedSettingsSchema
>;
